<script lang="ts">
import type { Component } from 'vue'
import type { UseComponentIconsProps } from '../composables/useComponentIcons'
import type { Color } from '../styles/colors'

export interface BadgeProps extends Omit<UseComponentIconsProps, 'loading' | 'loadingIcon'> {
  as?: string | Component
  label?: string | number
  color?: Color
  variant?: 'light' | 'outline' | 'solid' | 'dashed'
  size?: 'sm' | 'md'
  isRounded?: boolean
  isRemovable?: boolean
  class?: string | object
}

export interface BadgeSlots {
  leading?: (props?: object) => any
  default?: (props?: object) => any
  trailing?: (props?: object) => any
}

export interface BadgeEmits {
  remove: []
}

const ui = {
  base: 'font-medium inline-flex items-center transition-colors',
  label: 'truncate',
  leadingIcon: 'shrink-0',
  trailingIcon: 'shrink-0',
  size: {
    sm: 'text-xs',
    md: 'text-sm',
  },
  rounded: {
    sm: 'rounded-sm',
    md: 'rounded-md',
  },
  gap: 'gap-x-1',
  padding: {
    base: 'px-1.5 py-1',
    icon: 'p-1.5',
  },
  hover: 'cursor-pointer',
  variant: {
    light: {
      secondary: 'text-secondary-900 bg-secondary-200 border border-secondary-200',
      default: 'text-{color}-800 bg-{color}-100 border border-{color}-100',
      hover: {
        default: 'hover:bg-{color}-200 hover:border-{color}-200',
        secondary: 'hover:bg-secondary-300 hover:border-secondary-300',
      },
      icon: {
        remove: 'bg-transparent hover:bg-{color}-200 rounded-sm transition-colors',
      },
    },
    outline: {
      default: 'text-{color}-500 bg-white border border-{color}-500',
      secondary: 'text-secondary-900 bg-white border border-secondary-500',
      hover: {
        default: 'hover:bg-{color}-50',
      },
      icon: {
        remove: 'bg-transparent hover:bg-{color}-50 rounded-sm transition-colors',
      },
    },
    solid: {
      default: 'text-white bg-{color}-500 border border-{color}-500',
      secondary: 'text-white bg-secondary-900 border border-secondary-900',
      yellow: 'text-yellow-900 bg-yellow-500 border border-yellow-500',
      hover: {
        secondary: 'hover:bg-black hover:border-black',
        default: 'hover:bg-{color}-600 hover:border-{color}-600',
      },
      icon: {
        remove: 'bg-transparent hover:bg-{color}-400 rounded-sm transition-colors',
      },
    },
    dashed: {
      default: 'text-{color}-600 bg-white border border-dashed border-{color}-600',
      secondary: 'text-secondary-900 bg-white border border-dashed border-secondary-600',
      hover: {
        default: 'hover:bg-{color}-50',
      },
      icon: {
        remove: 'bg-transparent hover:bg-{color}-50 rounded-sm transition-colors',
      },
    },
  },
  icon: {
    base: 'flex-shrink-0',
    size: {
      sm: 'size-2.5',
      md: 'size-3',
    },
  },
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed, ref, useAttrs } from 'vue'
import { useComponentIcons } from '../composables/useComponentIcons'
import icons from '../styles/icons'
import AwIcon from './AwIcon.vue'

const props = withDefaults(defineProps<BadgeProps>(), {
  color: 'primary',
  size: 'md',
  as: 'span',
  variant: 'light',
  isRemovable: false,
})

const emit = defineEmits<BadgeEmits>()

const slots = defineSlots<BadgeSlots>()

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useComponentIcons(props)

const isVisible = ref(true)

const leadingIconClass = computed(() => [ui.icon.base, ui.leadingIcon, ui.icon.size[props.size]])
const trailingIconClass = computed(() => [ui.icon.base, ui.trailingIcon, ui.icon.size[props.size]])

const removeIconWrapperClass = computed(() => ['p-0.5', ui.variant[props.variant].icon.remove.replaceAll('{color}', props.color), 'cursor-pointer'])
const removeIconClass = computed(() => [ui.icon.base, ui.trailingIcon, ui.icon.size[props.size]])

const attrs = useAttrs()

const badgeClass = computed(() => [
  ui.base,
  props.class,
  ui.size[props.size],
  props.isRounded ? 'rounded-full' : ui.rounded[props.size],
  props.label || slots.default ? ui.padding.base : ui.padding.icon,
  ui.gap,
  props.color === 'secondary'
    ? ui.variant[props.variant].secondary
    : props.color === 'yellow' && props.variant === 'solid'
      ? ui.variant[props.variant].yellow
      : ui.variant[props.variant].default.replaceAll('{color}', props.color),
  attrs.onClick && (props.color === 'secondary' && (props.variant === 'light' || props.variant === 'solid')
    ? ui.variant[props.variant].hover.secondary
    : ui.variant[props.variant].hover.default.replaceAll('{color}', props.color)),
  attrs.onClick && ui.hover,
])

function handleRemove(e: Event) {
  e.stopPropagation()
  isVisible.value = false
  emit('remove')
}
</script>

<template>
  <Primitive v-if="isVisible" :as="as" :class="badgeClass">
    <slot name="leading">
      <AwIcon v-if="isLeading && leadingIconName" :name="leadingIconName" :class="leadingIconClass" />
    </slot>
    <slot>
      <span v-if="label !== undefined && label !== null" :class="ui.label">
        {{ label }}
      </span>
    </slot>
    <div v-if="isRemovable" :class="removeIconWrapperClass">
      <AwIcon :name="icons.closeBold" :class="removeIconClass" @click="handleRemove" />
    </div>
    <slot v-else name="trailing">
      <AwIcon v-if="isTrailing && trailingIconName" :name="trailingIconName" :class="trailingIconClass" />
    </slot>
  </Primitive>
</template>
